import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid, Cell } from "recharts";
import { Activity, HeartHandshake, Clock } from "lucide-react";

const PublicStats = () => {
  const [s, setS] = useState(null);
  const isEmbed = new URLSearchParams(window.location.search).get("embed") === "1";
  useEffect(() => { fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/stats`).then(r => r.json()).then(setS); }, []);

  return (
    <div>
      {!isEmbed && (
      <section className="border-b border-slate-200 bg-gradient-to-br from-slate-50 to-white">
        <div className="max-w-6xl mx-auto px-6 py-16">
          <div className="text-xs uppercase tracking-[0.18em] text-slate-500">Transparência RAF</div>
          <h1 className="font-display font-bold text-4xl md:text-5xl text-[#0B2B62] tracking-tight mt-2">Estatísticas públicas</h1>
          <p className="text-slate-600 mt-3 max-w-2xl">Dados agregados e anônimos dos atendimentos realizados pela plataforma RAF Emergência. Nenhuma informação pessoal é exposta.</p>
        </div>
      </section>
      )}

      <section className="max-w-6xl mx-auto px-6 py-10">
        {!s ? <p className="text-slate-500">Carregando dados...</p> : (
          <>
            <div className="grid md:grid-cols-3 gap-4 mb-8">
              <StatCard icon={HeartHandshake} label="Casos atendidos" value={s.total_cases} color="#0B2B62" />
              <StatCard icon={Activity} label="Internações concluídas" value={s.concluded} color="#16A34A" />
              <StatCard icon={Clock} label="Tempo médio até internação" value={s.avg_hours_to_admission != null ? `${s.avg_hours_to_admission} h` : "—"} color="#EA580C" />
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="public-chart-risk">
                <div className="text-xs uppercase tracking-widest text-slate-500 mb-3">Distribuição por classificação de risco</div>
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={s.by_risk.map(r => ({ ...r, label: r.level.charAt(0).toUpperCase()+r.level.slice(1) }))}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                    <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#64748B' }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 11, fill: '#64748B' }} axisLine={false} tickLine={false} width={28} />
                    <Tooltip cursor={{ fill: '#F8FAFC' }} contentStyle={{ borderRadius: 8, border: '1px solid #E2E8F0', fontSize: 12 }} />
                    <Bar dataKey="count" radius={[6,6,0,0]}>
                      {s.by_risk.map((entry) => {
                        const c = { emergencial: "#DC2626", alta: "#EA580C", moderada: "#EAB308", programado: "#16A34A" }[entry.level];
                        return <Cell key={entry.level} fill={c} />;
                      })}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>

              <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="public-chart-substance">
                <div className="text-xs uppercase tracking-widest text-slate-500 mb-3">Substâncias mais recorrentes</div>
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={s.by_substance} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" horizontal={false} />
                    <XAxis type="number" tick={{ fontSize: 11, fill: '#64748B' }} axisLine={false} tickLine={false} />
                    <YAxis type="category" dataKey="substance" tick={{ fontSize: 11, fill: '#64748B' }} axisLine={false} tickLine={false} width={100} />
                    <Tooltip contentStyle={{ borderRadius: 8, border: '1px solid #E2E8F0', fontSize: 12 }} />
                    <Bar dataKey="count" fill="#0B2B62" radius={[0,6,6,0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="bg-white border border-slate-200 rounded-xl p-5 mt-4" data-testid="public-chart-city">
              <div className="text-xs uppercase tracking-widest text-slate-500 mb-3">Distribuição geográfica (top 8)</div>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={s.by_city}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                  <XAxis dataKey="city" tick={{ fontSize: 11, fill: '#64748B' }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: '#64748B' }} axisLine={false} tickLine={false} width={28} />
                  <Tooltip contentStyle={{ borderRadius: 8, border: '1px solid #E2E8F0', fontSize: 12 }} />
                  <Bar dataKey="count" fill="#1FA0E5" radius={[6,6,0,0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>

            <p className="text-xs text-slate-400 mt-6 text-center">Dados atualizados em tempo real · Sem informação pessoal · LGPD</p>

            {!isEmbed && (
              <div className="mt-10 border-t border-slate-200 pt-8">
                <div className="text-xs uppercase tracking-widest text-slate-500 mb-3">Compartilhar</div>
                <p className="text-xs text-slate-500 mb-3">Selos SVG com dados reais em tempo real — cole no rodapé do site, LinkedIn, Instagram Story ou WhatsApp status.</p>
                <div className="grid md:grid-cols-3 gap-4">
                  {[
                    { metric: "concluded", label: "Internações realizadas", color: "16A34A" },
                    { metric: "total", label: "Casos atendidos", color: "0B2B62" },
                    { metric: "avg_hours", label: "Tempo médio até internação", color: "1FA0E5" },
                  ].map((b) => {
                    const url = `${process.env.REACT_APP_BACKEND_URL}/api/public/badge.svg?metric=${b.metric}&color=${b.color}`;
                    return (
                      <div key={b.metric} className="p-4 border border-slate-200 rounded-xl bg-white" data-testid={`badge-${b.metric}`}>
                        <div className="text-xs text-slate-500 mb-2">{b.label}</div>
                        <div className="mb-3 min-h-[40px] flex items-center overflow-x-auto">
                          <img src={url} alt={b.label} className="max-w-full h-8" />
                        </div>
                        <input readOnly value={url} onClick={(e) => e.target.select()}
                          className="w-full text-xs font-mono border border-slate-200 rounded-md px-2 py-1.5 bg-slate-50" />
                        <button
                          onClick={() => { navigator.clipboard.writeText(url); }}
                          data-testid={`badge-copy-${b.metric}`}
                          className="mt-2 text-xs text-[#0B2B62] hover:underline"
                        >Copiar URL</button>
                      </div>
                    );
                  })}
                </div>
                <p className="text-xs text-slate-500 mt-3">Copie a URL e use em Markdown (<code>{'![RAF](URL)'}</code>) ou como imagem em posts de LinkedIn/Instagram.</p>
              </div>
            )}
          </>
        )}
      </section>
    </div>
  );
};

const StatCard = ({ icon: Icon, label, value, color }) => (
  <div className="p-6 rounded-xl bg-white border border-slate-200">
    <Icon className="w-6 h-6" style={{ color }} strokeWidth={1.8} />
    <div className="text-xs uppercase tracking-widest text-slate-500 mt-3">{label}</div>
    <div className="font-display font-bold text-4xl mt-1" style={{ color }}>{value}</div>
  </div>
);

export default PublicStats;
